import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { 
    Shield, Clock, Heart, Brain, Users, CheckCircle, 
    ChevronDown, Star, ArrowRight, Lock,
    BookOpen, Award, Zap, Eye, X
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../components/ui/accordion";
import { toast } from "sonner";
import axios from "axios";
import Cookies from "js-cookie";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getVisitorId = () => {
    let visitorId = Cookies.get("visitor_id");
    if (!visitorId) {
        visitorId = `v_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        Cookies.set("visitor_id", visitorId, { expires: 365 });
    }
    return visitorId;
};

// Icon mapping for dynamic rendering
const iconMap = {
    Shield, Clock, Heart, Brain, Users, CheckCircle, 
    BookOpen, Award, Zap, Eye, Lock, Star, ArrowRight
};

const getIcon = (iconName) => {
    return iconMap[iconName] || CheckCircle;
};

const LandingPage = () => {
    const { productTag } = useParams();
    const [product, setProduct] = useState(null);
    const [testimonials, setTestimonials] = useState([]);
    const [liveStats, setLiveStats] = useState({ viewers: 15, purchases_today: 5 });
    const [countdown, setCountdown] = useState({ hours: 0, minutes: 0, seconds: 0 });
    const [email, setEmail] = useState("");
    const [couponCode, setCouponCode] = useState("");
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const [showPopup, setShowPopup] = useState(false);
    const [popupShown, setPopupShown] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [popupEmail, setPopupEmail] = useState("");
    const [popupEmailSubmitted, setPopupEmailSubmitted] = useState(false);
    const [notFound, setNotFound] = useState(false);
    
    // Order bump state
    const [bumpProduct, setBumpProduct] = useState(null);
    const [includeBump, setIncludeBump] = useState(false);
    const [showBumpModal, setShowBumpModal] = useState(false);
    
    // Funnel tracking state
    const [buySectionViewed, setBuySectionViewed] = useState(false);

    const visitorId = getVisitorId();
    const urlParams = new URLSearchParams(window.location.search);
    const previewEnabled = urlParams.get("preview") === "true";

    // Helper to get section data with fallbacks
    const getSection = (sectionName) => {
        return product?.sections?.[sectionName] || {};
    };

    // Track page view and funnel event
    useEffect(() => {
        if (!productTag || !product) return;
        
        const trackVisit = async () => {
            try {
                await axios.post(`${API}/track/pageview`, {
                    product_tag: productTag,
                    visitor_id: visitorId,
                    page: "landing",
                    referrer: document.referrer || null
                });
                await axios.post(`${API}/track/funnel`, {
                    product_tag: productTag,
                    visitor_id: visitorId,
                    event: "landing_view"
                });
            } catch (e) {
                console.error("Tracking error:", e);
            }
        };
        trackVisit();
    }, [productTag, product, visitorId]);

    // Fetch product data
    useEffect(() => {
        if (!productTag) return;
        
        // Use pre-rendered data if available (SSR fallback)
        if (window.__PRODUCT_DATA__ && window.__PRODUCT_DATA__.tag === productTag) {
            setProduct(window.__PRODUCT_DATA__);
            if (Array.isArray(window.__TESTIMONIALS_DATA__)) setTestimonials(window.__TESTIMONIALS_DATA__);
            delete window.__PRODUCT_DATA__;
            delete window.__TESTIMONIALS_DATA__;
            // Still fetch live stats and bump in background
            const fetchExtra = async () => {
                try {
                    const [statsRes, bumpRes] = await Promise.all([
                        axios.get(`${API}/product/${productTag}/stats/live`),
                        axios.get(`${API}/product/${productTag}/bump`)
                    ]);
                    setLiveStats(statsRes.data);
                    if (bumpRes.data.bump_enabled) setBumpProduct(bumpRes.data);
                } catch (e) {}
            };
            fetchExtra();
            return;
        }

        const fetchData = async () => {
            try {
                const [productRes, testimonialsRes, statsRes, bumpRes] = await Promise.all([
                    axios.get(`${API}/product/${productTag}`),
                    axios.get(`${API}/product/${productTag}/testimonials`),
                    axios.get(`${API}/product/${productTag}/stats/live`),
                    axios.get(`${API}/product/${productTag}/bump`)
                ]);
                setProduct(productRes.data);
                setTestimonials(testimonialsRes.data);
                setLiveStats(statsRes.data);
                
                // Set bump product if enabled
                if (bumpRes.data.bump_enabled) {
                    setBumpProduct(bumpRes.data);
                }

                // Seed testimonials if empty
                if (testimonialsRes.data.length === 0) {
                    try {
                        await axios.post(`${API}/admin/products/${productTag}/seed-testimonials`, {}, {
                            auth: { username: '', password: 'admin123' }
                        });
                        const newTestimonials = await axios.get(`${API}/product/${productTag}/testimonials`);
                        setTestimonials(newTestimonials.data);
                    } catch (e) {}
                }
            } catch (e) {
                console.error("Error fetching data:", e);
                if (e.response?.status === 404) {
                    setNotFound(true);
                }
            }
        };
        fetchData();
    }, [productTag]);

    // Update live stats periodically
    useEffect(() => {
        if (!productTag) return;
        
        const interval = setInterval(async () => {
            try {
                const res = await axios.get(`${API}/product/${productTag}/stats/live`);
                setLiveStats(res.data);
            } catch (e) {}
        }, 30000);
        return () => clearInterval(interval);
    }, [productTag]);

    // Countdown timer - persist across sessions
    useEffect(() => {
        if (!product?.discount_enabled) return;

        const COUNTDOWN_KEY = `hsi_countdown_${productTag}`;
        const countdownHours = product.countdown_hours || 2; // Default 2 hours
        let endTime;
        
        const storedEndTime = localStorage.getItem(COUNTDOWN_KEY);
        if (storedEndTime) {
            endTime = parseInt(storedEndTime, 10);
            if (endTime < Date.now()) {
                // Reset countdown
                endTime = Date.now() + countdownHours * 60 * 60 * 1000;
                localStorage.setItem(COUNTDOWN_KEY, endTime.toString());
            }
        } else if (product.discount_end_time) {
            endTime = new Date(product.discount_end_time).getTime();
            localStorage.setItem(COUNTDOWN_KEY, endTime.toString());
        } else {
            endTime = Date.now() + countdownHours * 60 * 60 * 1000;
            localStorage.setItem(COUNTDOWN_KEY, endTime.toString());
        }

        const updateCountdown = () => {
            const now = Date.now();
            const diff = Math.max(0, endTime - now);
            
            const hours = Math.floor(diff / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((diff % (1000 * 60)) / 1000);
            
            setCountdown({ hours, minutes, seconds });
        };

        updateCountdown();
        const interval = setInterval(updateCountdown, 1000);
        return () => clearInterval(interval);
    }, [product, productTag]);

    // Welcome popup (shows on load) or Exit intent detection
    useEffect(() => {
        if (!product?.exit_popup_enabled || popupShown) return;
        
        const popupType = product.popup_type || "exit";
        
        if (popupType === "welcome") {
            // Show welcome popup after a short delay
            const timer = setTimeout(() => {
                setShowPopup(true);
                setPopupShown(true);
                axios.post(`${API}/track/funnel`, {
                    product_tag: productTag,
                    visitor_id: visitorId,
                    event: "welcome_popup_shown"
                });
            }, 1500);
            return () => clearTimeout(timer);
        } else {
            // Exit intent detection
            const handleMouseLeave = (e) => {
                if (e.clientY <= 0 && !popupShown) {
                    setShowPopup(true);
                    setPopupShown(true);
                    axios.post(`${API}/track/funnel`, {
                        product_tag: productTag,
                        visitor_id: visitorId,
                        event: "exit_popup_shown"
                    });
                }
            };

            document.addEventListener("mouseleave", handleMouseLeave);
            return () => document.removeEventListener("mouseleave", handleMouseLeave);
        }
    }, [popupShown, visitorId, product?.exit_popup_enabled, product?.popup_type, productTag]);

    const handleApplyCoupon = async () => {
        if (!couponCode.trim()) return;
        try {
            const res = await axios.post(`${API}/product/${productTag}/validate-coupon?code=${couponCode}`);
            setAppliedCoupon(res.data);
            toast.success(`Coupon applied! ${res.data.discount_percent}% off`);
        } catch (e) {
            toast.error("Invalid coupon code");
            setAppliedCoupon(null);
        }
    };

    // Apply coupon from popup and scroll to pricing
    const handleApplyPopupCoupon = async () => {
        const popupCouponCode = popupSection?.coupon_code || "COMEBACK10";
        setCouponCode(popupCouponCode);
        setShowPopup(false);
        
        try {
            const res = await axios.post(`${API}/product/${productTag}/validate-coupon?code=${popupCouponCode}`);
            setAppliedCoupon(res.data);
            toast.success(`Coupon "${popupCouponCode}" applied! ${res.data.discount_percent}% off`);
            
            // Scroll to pricing section
            setTimeout(() => {
                const pricingElement = document.getElementById('pricing');
                if (pricingElement) {
                    pricingElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 100);
        } catch (e) {
            toast.error("Could not apply coupon automatically");
        }
    };

    const calculateFinalPrice = () => {
        if (!product) return 0;
        let price = product.discount_enabled ? product.discount_price : product.base_price;
        if (appliedCoupon) {
            price = price * (1 - appliedCoupon.discount_percent / 100);
        }
        return price.toFixed(2);
    };

    const calculateTotalWithBump = () => {
        let total = parseFloat(calculateFinalPrice());
        if (includeBump && bumpProduct) {
            total += bumpProduct.bump_price;
        }
        return total.toFixed(2);
    };

    // Calculate total assuming bump IS included (for modal display)
    const calculateTotalWithBumpForced = () => {
        let total = parseFloat(calculateFinalPrice());
        if (bumpProduct) {
            total += bumpProduct.bump_price;
        }
        return total.toFixed(2);
    };

    const handleCheckout = async (skipBumpModal = false, forceIncludeBump = null) => {
        if (!email || !email.trim()) {
            toast.error("Please enter your email address");
            return;
        }
        
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            toast.error("Please enter a valid email address");
            return;
        }
        
        // Determine if bump should be included (use forceIncludeBump if provided, otherwise use state)
        const shouldIncludeBump = forceIncludeBump !== null ? forceIncludeBump : includeBump;
        
        // If bump is modal display and we haven't shown it yet, show the modal
        if (!skipBumpModal && bumpProduct && bumpProduct.bump_display === "modal" && !shouldIncludeBump && !showBumpModal) {
            setShowBumpModal(true);
            // Track bump modal shown
            axios.post(`${API}/track/funnel`, {
                product_tag: productTag,
                visitor_id: visitorId,
                event: "bump_modal_shown",
                metadata: { bump_tag: bumpProduct.bump_tag, bump_price: bumpProduct.bump_price }
            });
            return;
        }
        
        setIsLoading(true);
        try {
            await axios.post(`${API}/track/funnel`, {
                product_tag: productTag,
                visitor_id: visitorId,
                event: "buy_click"
            });
            await axios.post(`${API}/track/funnel`, {
                product_tag: productTag,
                visitor_id: visitorId,
                event: "checkout_start"
            });

            const res = await axios.post(`${API}/product/${productTag}/checkout/create`, {
                product_tag: productTag,
                origin_url: window.location.origin,
                coupon_code: appliedCoupon?.code || null,
                email: email,
                include_bump: shouldIncludeBump
            });

            if (res.data.url) {
                window.location.href = res.data.url;
            }
        } catch (e) {
            toast.error("Error creating checkout session");
            setIsLoading(false);
        }
    };

    const handleBumpModalAccept = () => {
        setIncludeBump(true);
        setShowBumpModal(false);
        // Track bump accepted from modal
        axios.post(`${API}/track/funnel`, {
            product_tag: productTag,
            visitor_id: visitorId,
            event: "bump_selected",
            metadata: { bump_tag: bumpProduct.bump_tag, bump_price: bumpProduct.bump_price, source: "modal" }
        });
        // Proceed to checkout with bump included (pass true directly to avoid state race condition)
        handleCheckout(true, true);
    };

    const handleBumpModalDecline = () => {
        setShowBumpModal(false);
        // Track bump declined from modal
        axios.post(`${API}/track/funnel`, {
            product_tag: productTag,
            visitor_id: visitorId,
            event: "bump_declined",
            metadata: { bump_tag: bumpProduct.bump_tag, source: "modal" }
        });
        // Proceed to checkout without bump (pass false directly)
        handleCheckout(true, false);
    };

    // Track hero CTA click and scroll to pricing
    const handleHeroCtaClick = () => {
        axios.post(`${API}/track/funnel`, {
            product_tag: productTag,
            visitor_id: visitorId,
            event: "hero_cta"
        });
        document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" });
    };

    // Track when buy/pricing section is viewed (via Intersection Observer)
    useEffect(() => {
        if (!product || buySectionViewed) return;

        const pricingElement = document.getElementById("pricing");
        if (!pricingElement) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && !buySectionViewed) {
                        setBuySectionViewed(true);
                        axios.post(`${API}/track/funnel`, {
                            product_tag: productTag,
                            visitor_id: visitorId,
                            event: "buy_section_viewed"
                        });
                    }
                });
            },
            { threshold: 0.3 } // Trigger when 30% of pricing section is visible
        );

        observer.observe(pricingElement);

        return () => observer.disconnect();
    }, [product, productTag, visitorId, buySectionViewed]);

    const handlePopupClick = async () => {
        await axios.post(`${API}/track/funnel`, {
            product_tag: productTag,
            visitor_id: visitorId,
            event: "popup_click"
        });
        setShowPopup(false);
        document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" });
    };

    const handlePopupEmailSubmit = async () => {
        if (!popupEmail || !popupEmail.trim()) {
            toast.error("Please enter your email");
            return;
        }
        
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(popupEmail)) {
            toast.error("Please enter a valid email");
            return;
        }

        try {
            // Track popup email submission
            const popupEvent = product.popup_type === "welcome" ? "welcome_popup_click" : "exit_popup_click";
            await axios.post(`${API}/track/funnel`, {
                product_tag: productTag,
                visitor_id: visitorId,
                event: popupEvent
            });
            
            await axios.post(`${API}/product/${productTag}/lead/capture`, {
                product_tag: productTag,
                email: popupEmail,
                source: product.popup_type === "welcome" ? "welcome_popup" : "exit_popup"
            });
            setPopupEmailSubmitted(true);
            toast.success("You'll receive the discount code shortly!");
        } catch (e) {
            toast.error("Something went wrong");
        }
    };

    // Generate SEO meta data
    const getMetaDescription = () => {
        const heroSection = product?.sections?.hero || {};
        return heroSection.subheadline || product?.description || "Transform your life with science-backed techniques and proven methods.";
    };

    const getMetaTitle = () => {
        const heroSection = product?.sections?.hero || {};
        return heroSection.headline || product?.name || "The Human Science Institute";
    };

    const getMetaImage = () => {
        const heroSection = product?.sections?.hero || {};
        return heroSection.hero_image_url || "https://images.unsplash.com/photo-1643324896137-f0928e76202a?w=1200";
    };

    if (notFound) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="text-center">
                    <h1 className="font-heading text-4xl font-bold mb-4">Product Not Found</h1>
                    <p className="text-muted-foreground">The product you're looking for doesn't exist.</p>
                </div>
            </div>
        );
    }

    if (!product) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="animate-pulse">
                    <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
            </div>
        );
    }

    // Get section data
    const heroSection = getSection('hero');
    const authoritySection = getSection('authority');
    const featuresSection = getSection('features');
    const testimonialsSection = getSection('testimonials');
    const faqSection = getSection('faq');
    const pricingSection = getSection('pricing');
    const footerSection = getSection('footer');
    const popupSection = getSection('exit_popup');

    const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

    return (
        <div className="min-h-screen bg-background">
            {/* SEO Meta Tags */}
            <Helmet>
                <title>{getMetaTitle()} | {product.author_name}</title>
                <meta name="description" content={getMetaDescription()} />
                <meta name="keywords" content={`${product.name}, ebook, guide, self-improvement, ${product.author_name}`} />
                <link rel="canonical" href={currentUrl} />
                
                {/* Open Graph / Facebook */}
                <meta property="og:type" content="product" />
                <meta property="og:url" content={currentUrl} />
                <meta property="og:title" content={getMetaTitle()} />
                <meta property="og:description" content={getMetaDescription()} />
                <meta property="og:image" content={getMetaImage()} />
                <meta property="og:site_name" content={product.author_name} />
                <meta property="product:price:amount" content={product.discount_enabled ? product.discount_price : product.base_price} />
                <meta property="product:price:currency" content="USD" />
                
                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:url" content={currentUrl} />
                <meta name="twitter:title" content={getMetaTitle()} />
                <meta name="twitter:description" content={getMetaDescription()} />
                <meta name="twitter:image" content={getMetaImage()} />
            </Helmet>

            {/* Live Viewers Badge - Conditional */}
            {product.show_viewers_badge !== false && (
                <motion.div 
                    initial={{ opacity: 0, x: -50 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="fixed bottom-6 left-6 z-50 glass px-4 py-3"
                    data-testid="live-viewers-badge"
                >
                    <div className="flex items-center gap-3">
                        <div className="relative">
                            <Eye className="w-5 h-5 text-primary" />
                            <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                        </div>
                        <div className="text-sm">
                            <span className="text-primary font-mono font-bold">{liveStats.viewers}</span>
                            <span className="text-muted-foreground ml-1">viewing now</span>
                        </div>
                    </div>
                </motion.div>
            )}

            {/* Purchases Today Badge - Conditional */}
            {product.show_purchases_badge !== false && (
                <motion.div 
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="fixed bottom-6 right-6 z-50 glass px-4 py-3"
                    data-testid="purchases-badge"
                >
                    <div className="flex items-center gap-3">
                        <Zap className="w-5 h-5 text-accent" />
                        <div className="text-sm">
                            <span className="text-accent font-mono font-bold">{liveStats.purchases_today}</span>
                            <span className="text-muted-foreground ml-1">bought today</span>
                        </div>
                    </div>
                </motion.div>
            )}

            {/* Hero Section */}
            <section className="relative min-h-screen flex items-center overflow-hidden">
                <div 
                    className="absolute inset-0 bg-cover bg-center opacity-20"
                    style={{ 
                        backgroundImage: heroSection.hero_image_url 
                            ? `url(${heroSection.hero_image_url})`
                            : "url(https://images.unsplash.com/photo-1643324896137-f0928e76202a?w=1920)",
                        filter: "blur(2px)"
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/90 to-background" />
                
                <div className="relative max-w-7xl mx-auto px-6 py-32 lg:py-40">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8 }}
                        >
                            {/* Countdown Timer */}
                            {product.discount_enabled && (
                                <div className="mb-8" data-testid="countdown-timer">
                                    <p className="text-sm text-muted-foreground uppercase tracking-widest mb-3">
                                        Limited Time Offer Ends In
                                    </p>
                                    <div className="flex gap-4">
                                        {[
                                            { value: countdown.hours, label: "Hours" },
                                            { value: countdown.minutes, label: "Min" },
                                            { value: countdown.seconds, label: "Sec" }
                                        ].map((item, i) => (
                                            <div key={i} className="glass px-4 py-3 text-center min-w-[70px]">
                                                <div className="font-mono text-2xl font-bold text-primary">
                                                    {String(item.value).padStart(2, '0')}
                                                </div>
                                                <div className="text-xs text-muted-foreground uppercase">
                                                    {item.label}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight mb-6">
                                <span className="gold-text">{heroSection.headline || product.name}</span>
                            </h1>
                            
                            <p className="text-lg text-muted-foreground mb-8 max-w-xl">
                                {heroSection.subheadline || product.description}
                            </p>

                            <div className="flex flex-wrap gap-4 mb-8">
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <Shield className="w-4 h-4 text-primary" />
                                    <span>{heroSection.badge1_text || `${product.guarantee_days}-Day Money Back`}</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <Lock className="w-4 h-4 text-primary" />
                                    <span>{heroSection.badge2_text || "Secure Checkout"}</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <Clock className="w-4 h-4 text-primary" />
                                    <span>{heroSection.badge3_text || "Instant Access"}</span>
                                </div>
                            </div>

                            <Button 
                                onClick={handleHeroCtaClick}
                                className="btn-primary px-8 py-6 text-lg"
                                data-testid="hero-cta-button"
                            >
                                {heroSection.cta_text || "Get The Guide Now"}
                                <ArrowRight className="ml-2 w-5 h-5" />
                            </Button>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="relative hidden lg:block"
                        >
                            <div className="relative">
                                <div className="absolute inset-0 bg-primary/20 blur-3xl rounded-full" />
                                <img 
                                    src={product.product_image_url || "https://images.unsplash.com/photo-1620455970942-5fca5840d5ee?w=600"}
                                    alt={product.name}
                                    className="relative w-full max-w-md mx-auto opacity-80"
                                    style={{ mixBlendMode: 'lighten' }}
                                />
                            </div>
                        </motion.div>
                    </div>
                </div>

                <motion.div 
                    animate={{ y: [0, 10, 0] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="absolute bottom-10 left-1/2 -translate-x-1/2"
                >
                    <ChevronDown className="w-8 h-8 text-primary/50" />
                </motion.div>
            </section>

            {/* Authority Section */}
            <section className="py-24 relative">
                <div className="max-w-7xl mx-auto px-6">
                    <motion.div 
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <p className="text-primary uppercase tracking-widest text-sm mb-4">
                            {authoritySection.pretitle || "Backed by Science"}
                        </p>
                        <h2 className="font-heading text-3xl lg:text-4xl font-bold mb-6">
                            {authoritySection.title || `From ${product.author_name}`}
                        </h2>
                        <p className="text-muted-foreground max-w-2xl mx-auto">
                            {authoritySection.description || "With over 25 years of research, our team of experts has helped thousands transform their lives."}
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {(authoritySection.cards || [
                            { icon: "Award", title: "25+ Years Research", description: "Decades of clinical studies and real-world testing" },
                            { icon: "Users", title: "10,000+ Helped", description: "Proven results across diverse backgrounds" },
                            { icon: "BookOpen", title: "Evidence-Based", description: "Every technique backed by scientific literature" }
                        ]).map((item, i) => {
                            const IconComponent = getIcon(item.icon);
                            return (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: i * 0.1 }}
                                    className="card-landing p-8 text-center"
                                >
                                    <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center border border-primary/30">
                                        <IconComponent className="w-8 h-8 text-primary" />
                                    </div>
                                    <h3 className="font-heading text-xl font-bold mb-3">{item.title}</h3>
                                    <p className="text-muted-foreground text-sm">{item.description}</p>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* What You'll Learn Section */}
            <section className="py-24 bg-secondary/30">
                <div className="max-w-7xl mx-auto px-6">
                    <motion.div 
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <p className="text-primary uppercase tracking-widest text-sm mb-4">
                            {featuresSection.pretitle || "Inside The Guide"}
                        </p>
                        <h2 className="font-heading text-3xl lg:text-4xl font-bold mb-6">
                            {featuresSection.title || "What You'll Discover"}
                        </h2>
                    </motion.div>

                    <div className="grid md:grid-cols-2 gap-6">
                        {(featuresSection.features || [
                            { icon: "Brain", title: "Master Your Mind", description: "Overcome anxiety with proven psychological techniques" },
                            { icon: "Clock", title: "Proven Methods", description: "Science-backed approaches that deliver results" },
                            { icon: "Heart", title: "Deeper Connection", description: "Build unparalleled emotional and physical intimacy" },
                            { icon: "Zap", title: "Enhanced Experience", description: "Techniques for more fulfilling outcomes" },
                            { icon: "Users", title: "Better Communication", description: "How to discuss and explore together" },
                            { icon: "CheckCircle", title: "Practical Exercises", description: "Step-by-step training you can start immediately" }
                        ]).map((item, i) => {
                            const IconComponent = getIcon(item.icon);
                            return (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, x: i % 2 === 0 ? -30 : 30 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: i * 0.05 }}
                                    className="flex gap-5 p-6 card-landing"
                                >
                                    <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center border border-primary/30">
                                        <IconComponent className="w-6 h-6 text-primary" />
                                    </div>
                                    <div>
                                        <h3 className="font-heading text-lg font-bold mb-2">{item.title}</h3>
                                        <p className="text-muted-foreground text-sm">{item.description}</p>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>

                    {(product.preview_chapter_enabled || previewEnabled) && product.preview_chapter_url && (
                        <motion.div 
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className="text-center mt-12"
                        >
                            <Button 
                                onClick={() => setShowPreview(true)}
                                className="btn-secondary px-6 py-4"
                                data-testid="preview-chapter-button"
                            >
                                <BookOpen className="mr-2 w-5 h-5" />
                                Read Free Preview Chapter
                            </Button>
                        </motion.div>
                    )}
                </div>
            </section>

            {/* Testimonials Section */}
            <section className="py-24">
                <div className="max-w-7xl mx-auto px-6">
                    <motion.div 
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <p className="text-primary uppercase tracking-widest text-sm mb-4">
                            {testimonialsSection.pretitle || "Real Results"}
                        </p>
                        <h2 className="font-heading text-3xl lg:text-4xl font-bold mb-6">
                            {testimonialsSection.title || "What People Are Saying"}
                        </h2>
                    </motion.div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="testimonials-grid">
                        {(testimonials || []).map((testimonial, i) => (
                            <motion.div
                                key={testimonial.id || i}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.1 }}
                                className="card-landing p-6"
                            >
                                <div className="flex gap-1 mb-4">
                                    {[...Array(testimonial.rating || 5)].map((_, j) => (
                                        <Star key={j} className="w-4 h-4 fill-primary text-primary" />
                                    ))}
                                </div>
                                <p className="text-muted-foreground mb-4 italic">"{testimonial.text}"</p>
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-primary/20 flex items-center justify-center text-primary font-bold">
                                        {testimonial.name.charAt(0)}
                                    </div>
                                    <div>
                                        <p className="font-semibold text-sm">{testimonial.name}</p>
                                        <p className="text-muted-foreground text-xs">Age {testimonial.age}</p>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="py-24 bg-secondary/30">
                <div className="max-w-3xl mx-auto px-6">
                    <motion.div 
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <p className="text-primary uppercase tracking-widest text-sm mb-4">
                            {faqSection.pretitle || "Questions"}
                        </p>
                        <h2 className="font-heading text-3xl lg:text-4xl font-bold mb-6">
                            {faqSection.title || "Frequently Asked"}
                        </h2>
                    </motion.div>

                    <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
                        {(faqSection.items || [
                            { question: "Is this guide really science-based?", answer: "Absolutely. Every technique is backed by peer-reviewed research. We cite our sources and explain the science behind each method." },
                            { question: "How quickly will I see results?", answer: "Most people report noticeable improvements within 2-3 weeks of consistent practice. However, the guide includes techniques you can use immediately." },
                            { question: "Is the content discreet?", answer: "Yes. Your purchase will appear as 'HSI Digital' on your statement. The download is instant and private—no physical shipping required." },
                            { question: "What if it doesn't work for me?", answer: `We offer a full ${product.guarantee_days}-day money-back guarantee. If you don't see improvements, simply email us for a complete refund. No questions asked.` },
                            { question: "Is this guide suitable for all ages?", answer: "Yes. The techniques work regardless of age. Many of our most successful testimonials come from people who thought improvement wasn't possible." },
                            { question: "Can I share this with my partner?", answer: "We encourage it! The guide includes specific sections designed for couples to explore together." }
                        ]).map((item, i) => (
                            <AccordionItem key={i} value={`item-${i}`} className="card-landing border-0">
                                <AccordionTrigger className="px-6 py-4 text-left font-heading font-semibold hover:no-underline hover:text-primary">
                                    {item.question}
                                </AccordionTrigger>
                                <AccordionContent className="px-6 pb-4 text-muted-foreground">
                                    {item.answer}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </section>

            {/* Pricing Section */}
            <section id="pricing" className="py-24">
                <div className="max-w-4xl mx-auto px-6">
                    <motion.div 
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-12"
                    >
                        <p className="text-primary uppercase tracking-widest text-sm mb-4">
                            {pricingSection.pretitle || "Get Started Today"}
                        </p>
                        <h2 className="font-heading text-3xl lg:text-4xl font-bold mb-6">
                            {pricingSection.title || "Transform Your Life"}
                        </h2>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="card-landing p-8 lg:p-12 border-primary/30"
                        data-testid="pricing-card"
                    >
                        <div className="text-center mb-8">
                            <h3 className="font-heading text-2xl font-bold mb-4">{product.name}</h3>
                            
                            <div className="flex items-center justify-center gap-4 mb-2">
                                {product.discount_enabled && (
                                    <span className="text-muted-foreground line-through text-xl">
                                        ${product.base_price.toFixed(2)}
                                    </span>
                                )}
                                <span className="text-4xl font-heading font-bold gold-text">
                                    ${calculateFinalPrice()}
                                </span>
                            </div>
                            
                            {product.discount_enabled && (
                                <p className="text-sm text-accent">
                                    Save ${(product.base_price - product.discount_price).toFixed(2)} - Limited Time Only!
                                </p>
                            )}
                        </div>

                        <div className="grid sm:grid-cols-2 gap-4 mb-8">
                            {(pricingSection.features || [
                                { text: "Complete Guide" },
                                { text: "Step-by-Step Exercises" },
                                { text: "Partner Communication Scripts" },
                                { text: "Bonus Resources" },
                                { text: "Lifetime Updates" },
                                { text: `${product.guarantee_days}-Day Money Back Guarantee` }
                            ]).map((item, i) => (
                                <div key={i} className="flex items-center gap-3">
                                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                                    <span className="text-sm">{item.text}</span>
                                </div>
                            ))}
                        </div>

                        <div className="space-y-4">
                            <div>
                                <Input
                                    type="email"
                                    placeholder="Your email (required for download link)"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="h-12 bg-input border-border text-foreground placeholder:text-muted-foreground"
                                    data-testid="email-input"
                                    required
                                />
                                <p className="text-xs text-muted-foreground mt-1">* Required - we'll send your download link here</p>
                            </div>
                            
                            {product.coupon_field_enabled && (
                                <div className="flex gap-2">
                                    <Input
                                        type="text"
                                        placeholder="Coupon code (optional)"
                                        value={couponCode}
                                        onChange={(e) => setCouponCode(e.target.value)}
                                        className="h-12 bg-input border-border text-foreground placeholder:text-muted-foreground"
                                        data-testid="coupon-input"
                                    />
                                    <Button 
                                        onClick={handleApplyCoupon}
                                        className="btn-secondary h-12 px-6"
                                        data-testid="apply-coupon-button"
                                    >
                                        Apply
                                    </Button>
                                </div>
                            )}

                            {appliedCoupon && (
                                <p className="text-sm text-accent">
                                    Coupon "{appliedCoupon.code}" applied: {appliedCoupon.discount_percent}% off
                                </p>
                            )}

                            {/* Order Bump - Inline Display */}
                            {bumpProduct && bumpProduct.bump_display !== "modal" && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    onAnimationComplete={() => {
                                        // Track bump shown
                                        axios.post(`${API}/track/funnel`, {
                                            product_tag: productTag,
                                            visitor_id: visitorId,
                                            event: "bump_shown",
                                            metadata: { bump_tag: bumpProduct.bump_tag, bump_price: bumpProduct.bump_price }
                                        });
                                    }}
                                    className={`border-2 p-4 transition-all cursor-pointer ${
                                        includeBump 
                                            ? "border-primary bg-primary/10" 
                                            : "border-dashed border-accent/50 bg-accent/5 hover:border-accent"
                                    }`}
                                    onClick={() => {
                                        const newValue = !includeBump;
                                        setIncludeBump(newValue);
                                        // Track bump selection/deselection
                                        axios.post(`${API}/track/funnel`, {
                                            product_tag: productTag,
                                            visitor_id: visitorId,
                                            event: newValue ? "bump_selected" : "bump_deselected",
                                            metadata: { bump_tag: bumpProduct.bump_tag, bump_price: bumpProduct.bump_price }
                                        });
                                    }}
                                    data-testid="order-bump"
                                >
                                    <div className="flex items-start gap-3">
                                        <div className={`w-5 h-5 flex-shrink-0 border-2 flex items-center justify-center mt-0.5 ${
                                            includeBump ? "border-primary bg-primary" : "border-accent/50"
                                        }`}>
                                            {includeBump && <CheckCircle className="w-4 h-4 text-black" />}
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="font-heading font-bold text-accent">
                                                    ONE-TIME OFFER
                                                </span>
                                                <div className="flex items-center gap-2">
                                                    {bumpProduct.bump_has_discount && (
                                                        <span className="text-muted-foreground line-through text-sm">
                                                            ${bumpProduct.bump_original_price}
                                                        </span>
                                                    )}
                                                    <span className="font-mono font-bold text-accent">
                                                        +${bumpProduct.bump_price}
                                                    </span>
                                                </div>
                                            </div>
                                            <p className="font-semibold mb-1">{bumpProduct.bump_name}</p>
                                            <p className="text-sm text-muted-foreground">
                                                {bumpProduct.bump_description}
                                            </p>
                                        </div>
                                    </div>
                                </motion.div>
                            )}

                            {/* Total with bump - only show for inline display */}
                            {includeBump && bumpProduct && bumpProduct.bump_display !== "modal" && (
                                <div className="flex justify-between items-center py-2 border-t border-border">
                                    <span className="text-muted-foreground">Total:</span>
                                    <span className="font-mono font-bold text-lg gold-text">${calculateTotalWithBump()}</span>
                                </div>
                            )}

                            <Button 
                                onClick={() => handleCheckout()}
                                disabled={isLoading}
                                className="btn-primary w-full h-14 text-lg animate-pulse-glow"
                                data-testid="checkout-button"
                            >
                                {isLoading ? (
                                    <span className="flex items-center gap-2">
                                        <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                                        Processing...
                                    </span>
                                ) : (
                                    <>
                                        <Lock className="mr-2 w-5 h-5" />
                                        {pricingSection.cta_text || "Get Instant Access"} - ${includeBump && bumpProduct && bumpProduct.bump_display !== "modal" ? calculateTotalWithBump() : calculateFinalPrice()}
                                    </>
                                )}
                            </Button>

                            <p className="text-center text-xs text-muted-foreground">
                                Secure payment powered by Stripe. Your information is protected.
                            </p>
                        </div>
                    </motion.div>

                    {/* Guarantee Badge */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="mt-12 text-center"
                    >
                        <div className="inline-flex items-center gap-4 glass px-8 py-4">
                            <Shield className="w-12 h-12 text-primary" />
                            <div className="text-left">
                                <p className="font-heading font-bold">
                                    {pricingSection.guarantee_title || `${product.guarantee_days}-Day Money Back Guarantee`}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                    {pricingSection.guarantee_text || "Not satisfied? Full refund, no questions asked."}
                                </p>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 border-t border-border">
                <div className="max-w-7xl mx-auto px-6 text-center">
                    <p className="text-muted-foreground text-sm">
                        © {new Date().getFullYear()} {product.author_name}. {footerSection.copyright_text || "All rights reserved."}
                    </p>
                    <p className="text-muted-foreground text-xs mt-2">
                        {footerSection.disclaimer || "Results may vary. This guide is for educational purposes only."}
                    </p>
                </div>
            </footer>

            {/* Welcome/Exit Intent Popup */}
            <AnimatePresence>
                {showPopup && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6"
                        onClick={() => popupEmailSubmitted ? handleApplyPopupCoupon() : setShowPopup(false)}
                        data-testid="popup-modal"
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="card-landing max-w-lg w-full p-8 relative"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button 
                                onClick={() => popupEmailSubmitted ? handleApplyPopupCoupon() : setShowPopup(false)}
                                className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
                            >
                                <X className="w-6 h-6" />
                            </button>
                            
                            <div className="text-center">
                                {!popupEmailSubmitted ? (
                                    <>
                                        <h3 className="font-heading text-2xl font-bold mb-4">
                                            {popupSection.title || (product.popup_type === "welcome" ? "Welcome! Get 10% Off Today" : "Wait! Don't Leave Empty-Handed")}
                                        </h3>
                                        <p className="text-muted-foreground mb-6">
                                            {popupSection.description || "Get an exclusive 10% discount code sent to your email:"}
                                        </p>
                                        
                                        <div className="space-y-4 mb-6">
                                            <Input
                                                type="email"
                                                placeholder="Enter your email"
                                                value={popupEmail}
                                                onChange={(e) => setPopupEmail(e.target.value)}
                                                className="h-12 bg-input border-border text-foreground placeholder:text-muted-foreground"
                                                data-testid="popup-email-input"
                                            />
                                            <Button 
                                                onClick={handlePopupEmailSubmit}
                                                className="btn-primary w-full px-8 py-4"
                                                data-testid="popup-submit"
                                            >
                                                {popupSection.button_text || "Send Me The Discount"}
                                                <ArrowRight className="ml-2 w-5 h-5" />
                                            </Button>
                                        </div>
                                        
                                        <button
                                            onClick={handlePopupClick}
                                            className="text-sm text-muted-foreground hover:text-primary underline"
                                        >
                                            No thanks, I will pay full price
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <div className="w-16 h-16 mx-auto mb-6 bg-primary/20 flex items-center justify-center">
                                            <CheckCircle className="w-8 h-8 text-primary" />
                                        </div>
                                        <h3 className="font-heading text-2xl font-bold mb-4">
                                            {popupSection.success_title || "Check Your Inbox!"}
                                        </h3>
                                        <p className="text-muted-foreground mb-6">
                                            {popupSection.success_text || `Your exclusive discount code ${popupSection.coupon_code || "COMEBACK10"} is on its way.`}
                                        </p>
                                        <Button 
                                            onClick={handleApplyPopupCoupon}
                                            className="btn-primary px-8 py-4"
                                            data-testid="popup-apply-coupon"
                                        >
                                            Use It Now
                                            <ArrowRight className="ml-2 w-5 h-5" />
                                        </Button>
                                    </>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Preview Chapter Modal */}
            <AnimatePresence>
                {showPreview && product.preview_chapter_url && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-6"
                        onClick={() => setShowPreview(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="bg-card max-w-4xl w-full h-[80vh] relative"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button 
                                onClick={() => setShowPreview(false)}
                                className="absolute -top-12 right-0 text-foreground hover:text-primary"
                            >
                                <X className="w-8 h-8" />
                            </button>
                            <iframe 
                                src={product.preview_chapter_url}
                                className="w-full h-full"
                                title="Preview Chapter"
                            />
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Order Bump Modal - Shows before Stripe checkout */}
            <AnimatePresence>
                {showBumpModal && bumpProduct && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-6"
                        data-testid="bump-modal"
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.9, opacity: 0, y: 20 }}
                            className="card-landing max-w-lg w-full p-8 relative border-2 border-accent/50"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Urgency badge */}
                            <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-accent text-black px-4 py-1 font-bold text-sm uppercase tracking-wider">
                                Wait! Special One-Time Offer
                            </div>
                            
                            <div className="text-center pt-4">
                                {/* Value proposition */}
                                <h3 className="font-heading text-2xl lg:text-3xl font-bold mb-3">
                                    Complete Your Transformation
                                </h3>
                                <p className="text-muted-foreground mb-6">
                                    You are about to get <span className="text-foreground font-semibold">{product.name}</span>. 
                                    But wait... customers who added this bonus reported <span className="text-primary font-semibold">2x faster results</span>.
                                </p>

                                {/* Bump product card */}
                                <div className="bg-secondary/50 border border-primary/30 p-5 mb-6 text-left">
                                    <div className="flex items-center justify-between mb-3">
                                        <span className="font-heading font-bold text-lg">{bumpProduct.bump_name}</span>
                                        <div className="flex items-center gap-2">
                                            {bumpProduct.bump_has_discount && (
                                                <span className="text-muted-foreground line-through text-sm">
                                                    ${bumpProduct.bump_original_price}
                                                </span>
                                            )}
                                            <span className="text-2xl font-mono font-bold text-primary">
                                                ${bumpProduct.bump_price}
                                            </span>
                                        </div>
                                    </div>
                                    <p className="text-sm text-muted-foreground mb-3">
                                        {bumpProduct.bump_description}
                                    </p>
                                    <div className="flex items-center gap-2 text-sm">
                                        <CheckCircle className="w-4 h-4 text-primary" />
                                        <span>Instant download with your main purchase</span>
                                    </div>
                                </div>

                                {/* Savings highlight */}
                                <div className="bg-primary/10 border border-primary/30 p-3 mb-6">
                                    <p className="text-sm">
                                        <span className="font-bold text-primary">Bundle & Save:</span> Get both for just{' '}
                                        <span className="font-mono font-bold">${calculateTotalWithBumpForced()}</span>
                                        {bumpProduct.bump_has_discount && (
                                            <span className="text-muted-foreground ml-1">
                                                (saving ${(bumpProduct.bump_original_price - bumpProduct.bump_price).toFixed(2)})
                                            </span>
                                        )}
                                    </p>
                                </div>

                                {/* CTA buttons */}
                                <div className="space-y-3">
                                    <Button 
                                        onClick={handleBumpModalAccept}
                                        className="btn-primary w-full h-14 text-lg"
                                        data-testid="bump-modal-accept"
                                    >
                                        <Zap className="mr-2 w-5 h-5" />
                                        Yes! Add To My Order - ${calculateTotalWithBumpForced()}
                                    </Button>
                                    
                                    <button
                                        onClick={handleBumpModalDecline}
                                        className="w-full text-sm text-muted-foreground hover:text-foreground py-3 transition-colors"
                                        data-testid="bump-modal-decline"
                                    >
                                        No thanks, I will pass on this exclusive offer and pay ${calculateFinalPrice()} only
                                    </button>
                                </div>

                                {/* Trust element */}
                                <p className="text-xs text-muted-foreground mt-4">
                                    This offer is only available right now and will not be shown again.
                                </p>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default LandingPage;
